/**
 * Forgot password (FS-012).
 *
 * The server answers the same sentence whether or not the address has an
 * account, so this page does too — it shows that sentence and nothing that
 * would tell a stranger which emails belong to the business.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { apiClient } from "@/api/client";
import AuthShell from "@/components/auth/AuthShell";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const resp = await apiClient.post<{ message: string }>(
        "/api/v1/auth/password/forgot",
        { email: email.trim() },
      );
      setSent(resp.data.message);
    } catch {
      // Only a malformed address or a network failure lands here — the server
      // never refuses a well-formed one.
      setError("That didn't go through. Check the address and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter the email you sign in with and we'll send you a link to choose a new password."
      footer={
        <Link
          to="/login"
          className="font-medium text-brand-600 hover:text-brand-700"
        >
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <div className="flex gap-3" role="status">
          <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-success)]" />
          <div className="space-y-2 text-sm text-[var(--text-primary)]">
            <p>{sent}</p>
            <p className="text-[var(--text-secondary)]">
              Nothing after a few minutes? Check your spam folder, or ask your
              administrator to send you a link.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          {error && (
            <div className="rounded-[var(--radius-lg)] bg-[var(--fill-danger)] p-3 text-sm text-[var(--text-danger)]">
              {error}
            </div>
          )}
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            autoComplete="email"
            placeholder="you@example.com"
          />
          <Button
            type="submit"
            className="w-full"
            size="lg"
            isLoading={submitting}
          >
            Send reset link
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export default ForgotPasswordPage;
